import type { ErrorEvent } from "@sentry/core";
import type { CaptureResult } from "@posthog/types";

/**
 * Error message patterns that should be silently ignored and not reported
 * to any error tracking service. These are benign, unactionable errors
 * from browsers, extensions, or framework internals.
 *
 * This module is shared between:
 * - reportError() for explicitly reported errors
 * - PostHog before_send for auto-captured exceptions
 * - Sentry beforeSend for auto-captured exceptions
 */
const IGNORED_ERROR_PATTERNS: RegExp[] = [
  // Browser layout noise — benign, no stack trace, unactionable
  /ResizeObserver loop/,

  // Network errors — user connectivity issues, unactionable
  /Failed to fetch/,
  /NetworkError/,
  /Load failed/,

  // Cancelled requests — user navigated away, expected behavior
  /AbortError/,
  /TypeError: cancelled/,

  // Cross-origin script errors (usually browser extensions)
  /^Script error\.?$/,

  // Safari variant of cross-origin script error
  /^Javascript error: Script error\.? on line 0$/,

  // Next.js internal redirect mechanism (not a real error)
  /NEXT_REDIRECT/,

  // Stale JS chunks after deployments
  /ChunkLoadError/,
  /Loading chunk [\d]+ failed/,

  // Google Tag Manager + ad blocker conflict
  /^Cannot redefine property: googletag$/,

  // Random unactionable minified error seen in the wild
  /undefined is not an object \(evaluating 'a\.L'\)/,

  // Brave browser / Solana wallet extension trying to redefine window.solana
  /can't redefine non-configurable property "solana"/,

  // Google Tag Manager internal error, not affecting end-users
  /vv\(\)\.getRestrictions is not a function/,

  // Instagram in-app browser autofill handler — unactionable
  // https://developers.facebook.com/community/threads/320013549791141/
  /Can't find variable: _AutofillCallbackHandler/,

  // CEFSharp (.NET embedded Chromium) / Outlook link-preview crawler errors
  // https://github.com/getsentry/sentry-javascript/pull/14595
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,

  // Browser extension injected scripts — crypto wallets, Firefox-specific globals in Safari
  /window\.ethereum/,
  /Can't find variable: __firefox__/,
  /window\.__firefox__/,

  // Dark Reader browser extension injected script
  /Can't find variable: DarkReader/,
];

/**
 * Tests whether an error message matches any of the ignored error patterns.
 * Used by reportError(), PostHog before_send, and Sentry beforeSend to
 * consistently filter out noise across all error tracking pipelines.
 */
export const isIgnoredErrorMessage = (message: string): boolean =>
  IGNORED_ERROR_PATTERNS.some((pattern) => pattern.test(message));

/**
 * Sentry beforeSend hook that drops error events matching ignored patterns.
 * Pass this directly to the Sentry `init({ beforeSend })` option.
 */
export const sentryBeforeSend = (event: ErrorEvent): ErrorEvent | null => {
  const values = event.exception?.values || [];
  if (values.some((v) => isIgnoredErrorMessage(v.value || ''))) {
    return null;
  }
  return event;
};

/**
 * PostHog before_send hook that drops $exception events matching ignored patterns.
 * Pass this directly to the PostHog `init({ before_send })` option.
 */
export const posthogBeforeSend = (event: CaptureResult | null): CaptureResult | null => {
  if (event?.event === '$exception') {
    const exceptionList = event.properties?.['$exception_list'] || [];
    if (exceptionList.some((ex: Record<string, unknown>) =>
      isIgnoredErrorMessage(String(ex?.value || ''))
    )) {
      return null;
    }
  }
  return event;
};
